import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import type { UsageTenantRow } from '@libs/types';
import { Box, TableCell, TableRow, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { routes } from '@app/routes';
import { useTrans } from '@libs/i18n';

import { AdminStatusChip } from '../AdminStatusChip';
import { AdminTable, type AdminTableColumn } from '../AdminTable';
import { adminColors } from '../adminTheme';

import {
  formatCompactCount,
  formatUsd,
} from './usageFormat';
import { UsageNumber } from './UsageNumber';

interface Props {
  rows: UsageTenantRow[];
  isLoading?: boolean;
  isError?: boolean;
  footer?: React.ReactNode;
  skeletonRows?: number;
}

/**
 * Tenant spend leaderboard.
 *
 * Sentinel and deleted-tenant rows are rendered, never filtered. If they were
 * dropped, the sum of the visible rows would stop matching the platform total
 * and nobody could tell whether money vanished or was never there — so instead
 * they are labelled for what they are.
 */
export const UsageTenantsTable: FC<Props> = observer(
  ({ rows, isLoading, isError, footer, skeletonRows }) => {
    const { t } = useTrans();
    const theme = useTheme();
    const c = adminColors(theme);
    const navigate = useNavigate();

    const columns: AdminTableColumn[] = [
      { key: 'org', label: t('app.admin.usage.topTenants.col.org'), skeleton: 'title' },
      { key: 'cost', label: t('app.admin.usage.topTenants.col.cost'), align: 'right', skeleton: 'number' },
      { key: 'calls', label: t('app.admin.usage.topTenants.col.calls'), align: 'right', skeleton: 'number' },
      { key: 'tokens', label: t('app.admin.usage.topTenants.col.tokens'), align: 'right', skeleton: 'number' },
    ];

    const isReal = (row: UsageTenantRow) =>
      row.tenantStatus !== 'unattributed' && row.tenantStatus !== 'platform';

    return (
      <AdminTable
        columns={columns}
        isLoading={isLoading}
        isError={isError}
        errorLabel={t('app.admin.usage.topTenants.error')}
        isEmpty={!isLoading && rows.length === 0}
        skeletonRows={skeletonRows}
        footer={footer}
        emptyContent={
          // Empty is the EXPECTED state until the AI service ships metering, so
          // this says why rather than just "no data" — the difference between
          // "looks broken" and "looks correct on day one".
          <Box sx={{ px: 3, py: 5, textAlign: 'center' }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: c.ink, mb: 0.75 }}>
              {t('app.admin.usage.topTenants.empty')}
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: c.faint, maxWidth: 420, mx: 'auto' }}>
              {t('app.admin.usage.topTenants.emptyHint')}
            </Typography>
          </Box>
        }
      >
        {rows.map((row) => (
          <TableRow
            key={row.tenantId}
            hover={isReal(row)}
            onClick={
              isReal(row)
                ? () => navigate(routes.adminTenantDetail(row.tenantId, 'usage'))
                : undefined
            }
            sx={{ cursor: isReal(row) ? 'pointer' : 'default' }}
          >
            <TableCell>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: row.tenantStatus === 'deleted' ? c.faint : c.ink,
                    fontFamily: row.tenantStatus === 'deleted' ? 'monospace' : undefined,
                  }}
                >
                  {row.tenantStatus === 'deleted' ? row.tenantId.slice(0, 8) : row.tenantName}
                </Typography>
                {row.tenantStatus === 'deleted' && (
                  <Typography sx={{ fontSize: 11, color: c.faint }}>
                    {t('app.admin.usage.tenant.deleted')}
                  </Typography>
                )}
                {row.tenantStatus === 'disabled' && <AdminStatusChip status="suspended" />}
                {row.tenantStatus === 'unattributed' && (
                  <Typography sx={{ fontSize: 11, color: c.warn }}>
                    {t('app.admin.usage.tenant.unattributedHint')}
                  </Typography>
                )}
              </Box>
            </TableCell>
            <TableCell align="right">
              <UsageNumber sx={{ fontSize: 13, fontWeight: 600 }}>
                {formatUsd(row.costUsd)}
              </UsageNumber>
            </TableCell>
            <TableCell align="right">
              <UsageNumber sx={{ fontSize: 13 }}>{formatCompactCount(row.calls)}</UsageNumber>
            </TableCell>
            <TableCell align="right">
              <UsageNumber sx={{ fontSize: 13 }}>
                {formatCompactCount(row.totalTokens)}
              </UsageNumber>
            </TableCell>
          </TableRow>
        ))}
      </AdminTable>
    );
  },
);
